import * as React from "react";

import { cn } from "@/lib/utils/cn";

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Completion, 0–100. Clamped. */
  value: number;
  /** Track height utility (default "h-2"). */
  size?: string;
  /** Indicator fill class (default "bg-primary"). */
  indicatorClassName?: string;
}

/** A single-value progress bar. For multi-segment bars, compose `Progress`-style
 *  spans directly (see the questionnaire progress strip). */
export const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ value, size = "h-2", indicatorClassName, className, ...props }, ref) => {
    const pct = Math.max(0, Math.min(100, value));
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn("w-full overflow-hidden rounded-full bg-muted", size, className)}
        {...props}
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
            indicatorClassName ?? "bg-primary",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    );
  },
);
Progress.displayName = "Progress";
